import { ArrowDown, ArrowUp, ArrowUpDown, CalendarDays, ChevronRight, Globe, Tag, Upload, UserCheck } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { FormField } from '@/types/api/form'
import type { RecordListItem } from '@/types/api/record'
import { FormFieldType, RecordSourceType } from '@/types/enums'
import { cn } from '@/utils/cn'
import { formatDateTime } from '@/utils/date'
import { formatFieldValue } from '@/utils/field-formatters'

import { InlineCell } from './views/InlineCell'

export interface RecordSort {
  by: string
  dir: 'ASC' | 'DESC'
}

interface RecordsTableProps {
  records: RecordListItem[]
  /** Header of the title column: the form's main field label. */
  titleLabel: string
  /** Extra field columns: only the fields the list is filtered or sorted by. */
  columns: FormField[]
  sort: RecordSort
  onSortChange: (sort: RecordSort) => void
  busy?: boolean
  /** Spreadsheet mode: when given, cells for which {@link canEditCell} is true edit in place. */
  onCellEdit?: (record: RecordListItem, column: FormField, value: string) => Promise<void>
  canEditCell?: (record: RecordListItem, column: FormField) => boolean
}

export function SourceBadge({ source }: { source?: string }) {
  const config = {
    [RecordSourceType.INTERNAL_USER]: { label: 'Internal', cls: 'border-primary/20 bg-primary/10 text-primary', Icon: UserCheck },
    [RecordSourceType.PUBLIC_FORM]: { label: 'Public form', cls: 'border-border bg-secondary text-secondary-foreground', Icon: Globe },
    [RecordSourceType.CSV_IMPORT]: { label: 'CSV', cls: 'border-border bg-muted text-muted-foreground', Icon: Upload },
  }
  const c = source ? config[source as RecordSourceType] : config[RecordSourceType.INTERNAL_USER]
  const { label, cls, Icon } = c ?? config[RecordSourceType.INTERNAL_USER]
  return (
    <Badge variant="outline" className={cn('gap-1 px-2 py-0.5 text-xs font-medium', cls)}>
      <Icon aria-hidden="true" className="h-3 w-3" />
      {label}
    </Badge>
  )
}

/**
 * Match on the field's stable key, not its id or label: field ids change on every form version
 * save and labels are not unique, while the key survives re-saves (it is also the {{key}} used by
 * automation templates).
 */
export function cellValue(record: RecordListItem, column: FormField): string {
  const match = record.values.find((v) => v.formFieldKey === column.key)
  if (!match || match.value === '' || match.value == null) return '—'
  try {
    return formatFieldValue(match.value, column.type as FormFieldType)
  } catch {
    return match.value
  }
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSortChange,
  required,
}: {
  label: string
  sortKey: string
  sort: RecordSort
  onSortChange: (sort: RecordSort) => void
  required?: boolean
}) {
  const active = sort.by === sortKey
  const Icon = !active ? ArrowUpDown : sort.dir === 'ASC' ? ArrowUp : ArrowDown
  return (
    <TableHead
      className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
      aria-sort={active ? (sort.dir === 'ASC' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className="inline-flex items-center gap-1 uppercase hover:text-foreground"
        onClick={() => onSortChange({ by: sortKey, dir: active && sort.dir === 'DESC' ? 'ASC' : 'DESC' })}
      >
        {label}
        {required && <span className="text-destructive">*</span>}
        <Icon className={cn('h-3 w-3', !active && 'opacity-40')} />
      </button>
    </TableHead>
  )
}

/**
 * The records list: each row shows the record's title (its main field), tags, created date and
 * source. Field values appear only for the fields the list is filtered or sorted by, which are the
 * only values the server sends.
 */
export function RecordsTable({ records, titleLabel, columns, sort, onSortChange, busy, onCellEdit, canEditCell }: RecordsTableProps) {
  const navigate = useNavigate()
  // A Group column appears when sub-group records are included.
  const showGroup = new Set(records.map((r) => r.recordGroupId)).size > 1

  return (
    <Card className={cn('overflow-hidden shadow-none', busy && 'opacity-70 transition-opacity')} aria-busy={busy}>
      <div className="divide-y divide-border md:hidden">
        {records.map((record) => {
          const href = `/record-groups/${record.recordGroupId}/records/${record.recordId}`
          const detailColumns = columns.filter((column) => {
            const value = cellValue(record, column)
            return value !== '—' && value !== record.title
          })
          return (
            <Link
              key={record.recordId}
              to={href}
              className="block px-4 py-4 transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            >
              <div className="mb-3 flex items-start justify-between gap-3">
                <h2 className="min-w-0 flex-1 truncate text-base font-semibold text-foreground">
                  {record.title || 'Untitled record'}
                </h2>
                <ChevronRight aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
              </div>
              <dl className="space-y-2.5 text-sm">
                {showGroup && (
                  <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-start gap-2">
                    <dt className="text-muted-foreground">Group</dt>
                    <dd className="truncate text-foreground">{record.recordGroupName}</dd>
                  </div>
                )}
                {detailColumns.map((column) => (
                  <div key={column.key} className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-start gap-2">
                    <dt className="truncate text-muted-foreground">{column.label}</dt>
                    <dd className="break-words text-foreground">{cellValue(record, column)}</dd>
                  </div>
                ))}
                <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-start gap-2">
                  <dt className="flex items-center gap-1.5 text-muted-foreground"><Tag aria-hidden="true" className="h-4 w-4" />Tags</dt>
                  <dd>
                    {record.tags.length === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <span className="flex flex-wrap gap-1.5">
                        {record.tags.map((tag) => (
                          <Badge key={tag.tagId} variant="outline" className="gap-1.5 border-border px-2 py-0.5 text-xs font-medium text-foreground">
                            <span className="size-1.5 rounded-full" style={{ backgroundColor: tag.colorHex ?? 'var(--muted-foreground)' }} />
                            {tag.label}
                          </Badge>
                        ))}
                      </span>
                    )}
                  </dd>
                </div>
                <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-2">
                  <dt className="flex items-center gap-1.5 text-muted-foreground"><CalendarDays aria-hidden="true" className="h-4 w-4" />Created</dt>
                  <dd className="text-foreground">{formatDateTime(record.createdAt)}</dd>
                </div>
                <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-2">
                  <dt className="flex items-center gap-1.5 text-muted-foreground"><Globe aria-hidden="true" className="h-4 w-4" />Source</dt>
                  <dd><SourceBadge source={record.sourceType} /></dd>
                </div>
              </dl>
            </Link>
          )
        })}
      </div>
      <div className="hidden md:block">
      <Table>
        <TableHeader>
          <TableRow>
            <SortHeader label={titleLabel} sortKey="title" sort={sort} onSortChange={onSortChange} />
            {columns.map((col) => (
              <SortHeader
                key={col.key}
                label={col.label}
                sortKey={`field:${col.key}`}
                sort={sort}
                onSortChange={onSortChange}
                required={col.required}
              />
            ))}
            {showGroup && (
              <TableHead className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Group
              </TableHead>
            )}
            <TableHead className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Tags
            </TableHead>
            <SortHeader label="Created" sortKey="createdAt" sort={sort} onSortChange={onSortChange} />
            <TableHead className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Source
            </TableHead>
            <TableHead className="w-10 px-4 py-3" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((record) => {
            const href = `/record-groups/${record.recordGroupId}/records/${record.recordId}`
            return (
              <TableRow
                key={record.recordId}
                className="group cursor-pointer transition-colors hover:bg-accent/50"
                onClick={() => navigate(href)}
              >
                <TableCell className="max-w-[280px] truncate px-4 py-3 font-medium text-foreground" title={record.title ?? undefined}>
                  {record.title?.trim() ? record.title : <span className="font-normal italic text-muted-foreground">Untitled record</span>}
                </TableCell>
                {columns.map((col) => (
                  <TableCell key={col.key} className="max-w-[200px] truncate px-4 py-3 text-foreground" title={cellValue(record, col)}>
                    {onCellEdit && canEditCell?.(record, col) ? (
                      <InlineCell
                        field={col}
                        value={record.values.find((v) => v.formFieldKey === col.key)?.value ?? ''}
                        display={cellValue(record, col)}
                        onCommit={(next) => onCellEdit(record, col, next)}
                      />
                    ) : cellValue(record, col)}
                  </TableCell>
                ))}
                {showGroup && (
                  <TableCell className="max-w-[160px] truncate px-4 py-3 text-muted-foreground">
                    {record.recordGroupName}
                  </TableCell>
                )}
                <TableCell className="px-4 py-3">
                  <div className="flex max-w-[220px] flex-wrap gap-1">
                    {record.tags.length === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      record.tags.map((tag) => (
                        <Badge
                          key={tag.tagId}
                          variant="outline"
                          className="gap-1.5 border-border px-2 py-0.5 text-xs font-medium text-foreground"
                        >
                          <span className="size-1.5 rounded-full" style={{ backgroundColor: tag.colorHex ?? 'var(--muted-foreground)' }} />
                          {tag.label}
                        </Badge>
                      ))
                    )}
                  </div>
                </TableCell>
                <TableCell className="px-4 py-3 text-muted-foreground">{formatDateTime(record.createdAt)}</TableCell>
                <TableCell className="px-4 py-3">
                  <SourceBadge source={record.sourceType} />
                </TableCell>
                <TableCell className="px-4 py-3">
                  <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100" asChild>
                    <Link to={href} onClick={(e) => e.stopPropagation()} aria-label="Open record">
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      </div>
    </Card>
  )
}
